import { ProjectInstance, SupabaseProjectInstance } from '@/types/projectTypes';
import { ProjectService } from '../types';
import { v1, toResult, type V1Result } from '@/infrastructure/api/v1/client';
import { orgMemberToLegacy } from '@/infrastructure/api/v1/mappers/orgMembers';
import { projectToLegacy, type LegacyProject } from '@/infrastructure/api/v1/mappers/projects';
import { mapResult } from '@/modules/databaseService/scenes';

export class ProjectServiceImpl {
  constructor(private readonly projectService: ProjectService) {}

  async fetchProjectsForUser(userId: string): Promise<ProjectInstance[]> {
    return this.projectService.fetchProjectsForUser(userId);
  }

  async addProjectInstanceRow(newProjectInstance: SupabaseProjectInstance): Promise<string> {
    return this.projectService.addProjectInstanceRow(newProjectInstance);
  }

  async updateProjectPublishedStatus(projectId: string): Promise<any> {
    return this.projectService.updateProjectPublishedStatus(projectId);
  }

  async createNewProject(userId: string, navigate: any, projectName: string): Promise<void> {
    return this.projectService.createNewProject(userId, navigate, projectName);
  }

  async deleteProject(projectId: string): Promise<void> {
    return this.projectService.deleteProject(projectId);
  }
}

/** One project as its legacy project_instances row (getProject). */
export async function fetchProjectRow(projectId: string): Promise<V1Result<LegacyProject>> {
  return mapResult(await toResult(v1.GET('/api/v1/projects/{projectUniqueId}', { params: { path: { projectUniqueId: projectId } } })), projectToLegacy);
}

/** Recomputes and stores the project's published flag, story count and total duration (refreshProjectSummary). */
export async function refreshProjectSummary(projectId: string): Promise<V1Result<LegacyProject>> {
  const result = await toResult(v1.POST('/api/v1/projects/{projectUniqueId}/refresh-summary', { params: { path: { projectUniqueId: projectId } } }));
  return mapResult(result, projectToLegacy);
}

/** The signed-in user's org (getMyOrgMember). The membership read is always the caller's own. */
export const getUserOrg = async (): Promise<string> => {
  const { data, error } = await toResult(v1.GET('/api/v1/orgs/me/member'));
  if (error) throw error;
  return orgMemberToLegacy(data).org_instance_id;
};
